import { useState, useEffect } from "react";
import { useAuth } from "@api/useAuth";
import { useUsers, useUpdateUser, useClinics, useChangePassword } from "@api/hooks";
import {
  showSuccess,
  showError,
  Page,
  Card,
  Button,
  Title,
  Loading,
  CardInfo,
  Badge,
  Modal,
  UpdateInformation,
  ListData,
  UserCard,
  Pagination,
  FormField,
  SearchInput,
} from "@common";
import {
  getUserInfoItems,
  FORM_IDS,
  getPermissionsItems,
  changePasswordSchema,
  isObserver,
  isOrganizerOrAdmin,
  getAssignableRoleOptions,
} from "@utils";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormInteractionTime } from "@hooks/useFormInteractionTime";

export default function Profile() {
  const { user, isLoading } = useAuth();
  const [showUserList, setShowUserList] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [expandedList, setExpandedList] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [userSearch, setUserSearch] = useState("");

  // NEW: toggle for inline change password form
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const {
    formTrackingProps: passwordFormTrackingProps,
    recordSubmitSuccess: recordPasswordFormSuccess,
    reset: resetPasswordInteractionTimer,
  } = useFormInteractionTime(FORM_IDS.PROFILE_CHANGE_PASSWORD);

  useEffect(() => {
    if (!showPasswordForm) resetPasswordInteractionTimer();
  }, [showPasswordForm, resetPasswordInteractionTimer]);

  // Check if user can manage users (organizer or admin)
  const canManageUsers = isOrganizerOrAdmin(user);
  // Observer can browse the user list (View only, no Edit)
  const canViewUsers = canManageUsers || isObserver(user);

  // Fetch users using hook with pagination and optional search
  const searchTerm = userSearch.trim() || undefined;
  const {
    data: usersData,
    isLoading: isLoadingUsers,
    refetch: fetchUsers,
  } = useUsers({
    limit: itemsPerPage,
    offset: (currentPage - 1) * itemsPerPage,
    ...(searchTerm ? { search: searchTerm } : {}),
  });

  // Fetch clinics for dropdown
  const { data: clinicsData } = useClinics();

  // Fetch users when showUserList, pagination, or search changes
  useEffect(() => {
    if (canViewUsers && showUserList) {
      fetchUsers();
    }
  }, [canViewUsers, showUserList, currentPage, itemsPerPage, searchTerm, fetchUsers]);

  const updateUserMutation = useUpdateUser();

  // ==========================
  // Change Password (inline)
  // ==========================
  const changePasswordMutation = useChangePassword();

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPasswordForm,
    formState: { errors: passwordErrors, isSubmitting: isUpdatingPassword },
  } = useForm({
    resolver: zodResolver(changePasswordSchema),
    mode: "onSubmit",
    defaultValues: {
      old_password: "",
      new_password: "",
      new_password_confirm: "",
    },
  });

  async function handleChangePassword(values) {
    try {
      const res = await changePasswordMutation.mutateAsync({
        old_password: values.old_password,
        new_password: values.new_password,
        new_password_confirm: values.new_password_confirm,
      });

      recordPasswordFormSuccess();

      showSuccess(res?.message || "Password updated successfully");

      // close + reset after success
      resetPasswordForm();
      setShowPasswordForm(false);
    } catch (error) {
      const data = error?.response?.data;

      // backend can return object field errors or string errors
      const msg =
        data?.old_password?.[0] ||
        data?.new_password?.[0] ||
        data?.new_password_confirm?.[0] ||
        data?.non_field_errors?.[0] ||
        data?.detail ||
        data?.message ||
        (typeof data === "string" ? data : null) ||
        "Failed to update password";

      showError(msg);
    }
  }

  // Sort users by role when data changes
  const users = usersData?.results || usersData || [];
  const sortedUsers = [...users].sort((a, b) => {
    if (!a.role && !b.role) return 0;
    if (!a.role) return 1;
    if (!b.role) return -1;
    return a.role - b.role;
  });

  async function handleUpdateUser(userId, updateData) {
    try {
      await updateUserMutation.mutateAsync({ id: userId, data: updateData });
      showSuccess("User updated successfully");
      await fetchUsers();
      setSelectedUser(null);
    } catch (error) {
      const data = error?.response?.data;
      // DRF returns field-level errors as { field: ["msg"] } or { detail: "msg" }
      let msg = "Failed to update user";
      if (data) {
        if (typeof data === "string") {
          msg = data;
        } else if (data.detail) {
          msg = data.detail;
        } else if (data.non_field_errors) {
          msg = data.non_field_errors[0];
        } else {
          // Collect field-level errors
          const fieldErrors = Object.entries(data)
            .map(([field, errors]) => {
              const errorMsg = Array.isArray(errors) ? errors[0] : errors;
              return `${field}: ${errorMsg}`;
            })
            .join("; ");
          if (fieldErrors) msg = fieldErrors;
        }
      }
      showError(msg);
      throw error;
    }
  }

  function handlePageChange(newPage) {
    setCurrentPage(newPage);
    setSelectedUser(null);
    setViewingUser(null);
  }

  function handlePageSizeChange(newSize) {
    setItemsPerPage(newSize);
    setCurrentPage(1);
    setSelectedUser(null);
    setViewingUser(null);
  }

  function handleUpdateUserInfo(formData) {
    if (selectedUser) {
      return handleUpdateUser(selectedUser.id, formData);
    }
    return Promise.resolve();
  }

  function handleSelectUser(selectedUser) {
    setSelectedUser(selectedUser);
  }

  if (isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center min-h-screen">
          <Loading size="lg" text="Loading profile..." />
        </div>
      </Page>
    );
  }

  if (!user) {
    return (
      <Page>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-smiles-red">User not found. Please log in.</div>
        </div>
      </Page>
    );
  }

  const userInfoItems = getUserInfoItems(user);
  const permissionsItems = getPermissionsItems(user);

  return (
    <Page>
      <div className="max-w-4xl mx-auto py-8 px-4">
        <Title level={1} className="mb-8">
          My Profile
        </Title>

        {/* User Information Card (custom here to add inline button) */}
        <Card className="mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Title level={2}>User Information</Title>

              <Button variant="outline" size="sm" onClick={() => setShowPasswordForm((v) => !v)}>
                {showPasswordForm ? "Hide Password Form" : "Change Password"}
              </Button>
            </div>

            <div className="space-y-3">
              {userInfoItems.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-smiles-gray">{item.label}</span>

                  {item.badge ? (
                    <Badge color={item.badgeColorKey} className={item.badgeColor}>
                      {item.value}
                    </Badge>
                  ) : (
                    <span className="text-sm text-smiles-darkGray">{item.value}</span>
                  )}
                </div>
              ))}
            </div>

            {/* Inline Change Password form (only when button clicked) */}
            {showPasswordForm && (
              <div className="mt-6 pt-6 border-t border-palette-gray-divider">
                <Title level={3} className="mb-4">
                  Update Password
                </Title>

                <form
                  onSubmit={handlePasswordSubmit(handleChangePassword)}
                  className="space-y-4"
                  {...passwordFormTrackingProps}
                >
                  <FormField
                    label="Current Password"
                    name="old_password"
                    type="password"
                    placeholder="Enter current password"
                    register={registerPassword}
                    error={passwordErrors.old_password?.message}
                    required={true}
                  />

                  <FormField
                    label="New Password"
                    name="new_password"
                    type="password"
                    placeholder="Enter new password"
                    register={registerPassword}
                    error={passwordErrors.new_password?.message}
                    required={true}
                  />

                  <FormField
                    label="Confirm New Password"
                    name="new_password_confirm"
                    type="password"
                    placeholder="Confirm new password"
                    register={registerPassword}
                    error={passwordErrors.new_password_confirm?.message}
                    required={true}
                  />

                  <div className="flex items-center gap-3">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isUpdatingPassword || changePasswordMutation.isPending}
                    >
                      {isUpdatingPassword || changePasswordMutation.isPending
                        ? "Updating..."
                        : "Update Password"}
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        resetPasswordForm();
                        setShowPasswordForm(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </Card>

        {/* Permissions Card (unchanged) */}
        <CardInfo title="Permissions" className="mb-6" items={permissionsItems} />

        {/* User Management Section (Only for Organizer/Admin) */}
        {canViewUsers && (
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <Title level={2}>Manage Users</Title>
                <Button variant="outline" onClick={() => setShowUserList(!showUserList)}>
                  {showUserList ? "Hide Users" : "Show Users"}
                </Button>
              </div>

              <p className="text-sm text-smiles-lightGray mb-4">
                As an {user.role_display}, you can view and manage all users in the system.
              </p>

              {showUserList && (
                <div className="mt-4">
                  <div className="mb-4">
                    <SearchInput
                      value={userSearch}
                      onChange={(value) => {
                        setUserSearch(value);
                        setCurrentPage(1);
                      }}
                      placeholder="Search by name or email"
                    />
                  </div>
                  {isLoadingUsers ? (
                    <div className="text-center py-4">
                      <Loading size="md" text="Loading users..." />
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-end mb-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setExpandedList((v) => !v)}
                        >
                          {expandedList ? "Collapse List" : "Expand List"}
                        </Button>
                      </div>
                      <ListData
                        items={sortedUsers}
                        currentItemId={user?.id}
                        emptyMessage="No users found"
                        className={expandedList ? "overflow-y-auto" : "max-h-96 overflow-y-auto"}
                        renderItem={(u, isCurrentItem) => (
                          <>
                            <UserCard user={u} isCurrentUser={isCurrentItem} showBadge={true} />
                            <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setViewingUser(u);
                                }}
                              >
                                View
                              </Button>
                              {canManageUsers && (
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectUser(u);
                                  }}
                                >
                                  Edit
                                </Button>
                              )}
                            </div>
                          </>
                        )}
                      />

                      {sortedUsers.length > 0 && (
                        <div className="mt-4 flex justify-center">
                          <Pagination
                            page={currentPage}
                            pageSize={itemsPerPage}
                            total={usersData?.count ?? sortedUsers.length}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            pageSizeOptions={[10, 20, 50, 100]}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </Card>
        )}

        {/* User View Modal */}
        <Modal
          isOpen={!!viewingUser}
          onClose={() => setViewingUser(null)}
          title={viewingUser ? `User: ${viewingUser.full_name}` : ""}
          size="md"
        >
          {viewingUser && (
            <div className="px-6 pb-6 space-y-3">
              {getUserInfoItems(viewingUser).map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-smiles-gray">{item.label}</span>
                  {item.badge ? (
                    <Badge color={item.badgeColorKey} className={item.badgeColor}>
                      {item.value}
                    </Badge>
                  ) : (
                    <span className="text-sm text-smiles-darkGray">{item.value}</span>
                  )}
                </div>
              ))}
              <div className="flex justify-end pt-4 border-t border-palette-gray-divider">
                <Button variant="outline" onClick={() => setViewingUser(null)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* User Edit Modal */}
        <UpdateInformation
          isOpen={!!selectedUser}
          onClose={() => setSelectedUser(null)}
          onUpdate={handleUpdateUserInfo}
          data={
            selectedUser
              ? {
                  first_name: selectedUser.first_name || "",
                  last_name: selectedUser.last_name || "",
                  email: selectedUser.email,
                  role: selectedUser.role,
                  clinic: selectedUser.clinic || [],
                  phone_number: selectedUser.phone_number || "",
                  status: selectedUser.status,
                }
              : null
          }
          title={selectedUser ? `Edit User: ${selectedUser.full_name}` : ""}
          isLoading={updateUserMutation.isPending}
          fieldConfig={{
            role: {
              type: "dropdown",
              label: "Role",
              placeholder: "Select a role",
              options: getAssignableRoleOptions(user),
              required: true,
            },
            clinic: {
              type: "multi-dropdown",
              label: "Clinics",
              placeholder: "Select clinics",
              options: (clinicsData?.results || clinicsData || []).map((clinic) => ({
                value: clinic.clinic,
                label: clinic.name?.trim() ? `${clinic.name} (${clinic.clinic})` : clinic.clinic,
              })),
              required: false,
            },
            status: {
              type: "dropdown",
              label: "Status",
              placeholder: "Select status",
              options: [
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
                { value: "suspended", label: "Suspended" },
              ],
              required: true,
            },
          }}
        />
      </div>
    </Page>
  );
}
